import React, { Fragment, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { NetworkStatus } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Button } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';

import { GQLParty, GQLPieceAsset } from '@jewelutions/core-crm-server-api-client-domain';

import { userLanguage } from '../../../../core/AuthContext';
import { JwlTable, LoadingSpinner, ErrorComponent, Chip } from '../../../../components';

import { GQLOperationBuilder } from '../../../../operations/GQLOperationBuilder';
import PieceCreateDialog from '../PieceCreateDialog';
import { PlatformNavigation } from '../../navigation/PlatformNavigation';
import { successNotification } from '../../../../core/utils';
import { toast } from 'react-toastify';

interface IProps {
  party: GQLParty;
}

const PiecesList: React.FC<IProps> = ({ party }: IProps) => {
  const history = useHistory();
  const match: any = useRouteMatch();
  const [errorComponentState, setErrorComponentState] = useState(false);
  const { t: translate } = useTranslation();
  const [isCreatePieceDialogOpen, setIsCreatePieceDialogOpen] = useState(false);

  const { data, loading, error, networkStatus, refetch } = GQLOperationBuilder.piece.getPiecesByPlatform();

  const reloadData = () => {
    refetch().then();
  };

  if (loading || networkStatus === NetworkStatus.refetch) return <LoadingSpinner />;

  if (error && !errorComponentState)
    return (
      <ErrorComponent
        close={setErrorComponentState}
        details={error?.stack}
        callback={reloadData}
        message={error.message}
      />
    );

  const columns = ['Name', 'Publishable', 'Geometry', 'Publishable', 'Owner ID'];

  const getPiecesRows = () => {
    return GQLOperationBuilder.piece.pieceAssetArrayFromResponse(data).map((piece: GQLPieceAsset) => {
      return {
        id: piece.id,
        cells: [
          piece.piece.title[userLanguage],
          piece.piece.description[userLanguage],
          piece.piece.geometryType,
          <Chip
            size="small"
            mr={1}
            mb={1}
            label={piece.isPublishable ? 'YES' : 'NO'}
            $active={piece.isPublishable}
          />,
          piece.ownerPartyId
        ]
      };
    });
  };

  const navigateToPieceDetails = (pieceId: string) => {
    history.push(PlatformNavigation.pieceDetails.pathFromParams({ pieceId: pieceId }));
  };

  return (
    <Fragment>
      <JwlTable
        title="Platform pieces list"
        description={`The list of all piece geometries defined in the system.`}
        columns={columns}
        rows={getPiecesRows()}
        onTableRowClick={navigateToPieceDetails}
        children={
          <Button
            variant="contained"
            color={'primary'}
            onClick={() => setIsCreatePieceDialogOpen(true)}
            style={{ marginLeft: '4px' }}
          >
            <AddIcon />
            <span style={{ marginLeft: '4px' }}>Add Piece</span>
          </Button>
        }
      />
      {isCreatePieceDialogOpen && (
        <PieceCreateDialog
          party={party}
          onClose={success => {
            if (success === true) {
              successNotification(toast, 'Piece created successfully', () => {});
            }

            setIsCreatePieceDialogOpen(false);
            reloadData();
          }}
        />
      )}
    </Fragment>
  );
};

export default PiecesList;
